import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PROPERTY_TYPES,
  OPERATION_TYPES,
  ESTADO_OPTIONS,
  CURRENCIES,
} from "../utils/formatters";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { usePropertyForm } from "../hooks/usePropertyForm";
import { canUseAiDescriptions } from "../constants/plans";
import { PropertiesLimitModal } from "../components/modals/PropertiesLimitModal";
import BackNavigationButton from "../components/navigation/BackNavigationButton";
import { useFormDraft } from "../hooks/useFormDraft";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";

const getDefaultPropertyDraft = () => ({
  title: "",
  description: "",
  price: "",
  currency: "MXN",
  price_on_request: false,
  category: "Residencial",
  property_type: "",
  estado: "",
  operation_types: ["venta"],
  address: "",
  location_type: "",
  geometry: {
    type: "Point",
    coordinates: [-99.1332, 19.4326],
  },
  land_size_m2: "",
  construction_size_m2: "",
  bedrooms: "",
  bathrooms: "",
  half_bathrooms: "",
  parking: "",
  features: "",
  amenities: "",
  image_urls: "",
  cover_image_url: "",
  developer_id: "",
  development_id: "",
  allow_broker_representation: false,
  commission_percentage: 3.0,
  max_brokers: 3,
});

const AddProperty = () => {
  const navigate = useNavigate();
  const {
    user,
    loading,
    generatingAI,
    developerProfile,
    checkingLimit,
    myDevelopments,
    formData,
    setFormData,
    generateWithAI,
    handleSubmit,
    showPropertyLimitModal,
    setShowPropertyLimitModal,
    limitModalConfig,
    draftStorageKey,
  } = usePropertyForm();
  const normalizedFormData = useMemo(() => {
    const { id, ...rest } = formData;
    return rest;
  }, [formData]);
  const isDirty = useMemo(
    () =>
      JSON.stringify(normalizedFormData) !==
      JSON.stringify(getDefaultPropertyDraft()),
    [normalizedFormData],
  );
  useFormDraft({
    draftKey: draftStorageKey,
    formData,
    setFormData,
    enabled: !checkingLimit,
  });
  const { confirmIfDirty } = useUnsavedChangesGuard(isDirty);

  if (checkingLimit) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const handleCancel = () => {
    if (!confirmIfDirty()) return;
    navigate("/dashboard");
  };

  return (
    <div className="min-h-screen bg-muted">
      <PropertiesLimitModal
        isOpen={showPropertyLimitModal}
        onClose={() => setShowPropertyLimitModal(false)}
        config={limitModalConfig}
      />
      <Header />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <BackNavigationButton
          className="mb-4 hover:bg-muted"
          onBeforeNavigate={confirmIfDirty}
          dataTestId="add-property-back"
        />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-6 md:mb-8">
            <h1
              className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 tracking-tight"
              style={{ fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}
              data-testid="add-property-title"
            >
              Nueva Propiedad
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Completa la información de tu propiedad
            </p>
            {developerProfile && (
              <div className="mt-4 bg-accent/10 border border-accent/30 rounded-lg p-4">
                <p className="text-sm">
                  ✓ Esta propiedad se asociará automáticamente a tu perfil de{" "}
                  <strong>{developerProfile.name}</strong>
                </p>
              </div>
            )}
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-8 bg-background rounded-lg p-4 sm:p-6 md:p-8"
            data-testid="add-property-form"
          >
            <div className="space-y-6">
              <h2
                className="text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Información Básica
              </h2>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Título *
                </label>
                <Input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="Ej: Hermosa casa en el centro"
                  required
                  data-testid="title-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium block">
                    Descripción *
                  </label>
                  {canUseAiDescriptions(user) ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={generateWithAI}
                      disabled={generatingAI}
                      className="h-8 text-xs"
                    >
                      {generatingAI ? "⏳ Generando..." : "🤖 Generar con IA"}
                    </Button>
                  ) : (
                    <div className="relative group">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        disabled
                        className="h-8 text-xs opacity-50 cursor-not-allowed"
                      >
                        🤖 Generar con IA
                      </Button>
                      <div className="absolute right-0 top-full mt-2 hidden group-hover:block z-10">
                        <div className="bg-gray-900 text-white text-xs rounded py-2 px-3 whitespace-nowrap">
                          Disponible en Plan Pro
                          <div className="mt-1">
                            <button
                              onClick={() => navigate("/pricing")}
                              className="underline hover:text-accent"
                            >
                              Ver Planes
                            </button>
                          </div>
                          <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-b-gray-900"></div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <Textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Describe tu propiedad... o genera automáticamente con IA"
                  rows={4}
                  required
                  data-testid="description-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">Precio</label>
                <div className="mb-3 rounded-md border border-border/70 bg-muted/40 p-3">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.price_on_request}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          price_on_request: e.target.checked,
                          ...(e.target.checked ? { price: "" } : {}),
                        })
                      }
                      className="mt-1 h-4 w-4"
                      data-testid="price-on-request-checkbox"
                    />
                    <span>
                      <span className="font-medium text-sm block">
                        Publicar como "Precio a consultar"
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Ideal para propiedades premium con negociación
                        personalizada.
                      </span>
                    </span>
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium mb-2 block">
                      Precio
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                      placeholder={
                        formData.price_on_request
                          ? "Precio oculto al público"
                          : "0.00"
                      }
                      disabled={formData.price_on_request}
                      data-testid="price-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Moneda
                    </label>
                    <Select
                      value={formData.currency}
                      onValueChange={(val) =>
                        setFormData({ ...formData, currency: val })
                      }
                    >
                      <SelectTrigger data-testid="currency-select">
                        <SelectValue placeholder="Moneda" />
                      </SelectTrigger>
                      <SelectContent>
                        {CURRENCIES.map((c) => (
                          <SelectItem key={c.value} value={c.value}>
                            {c.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2
                className="text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Categorización
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Categoría *
                  </label>
                  <Select
                    value={formData.category}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        category: value,
                        property_type: "",
                      })
                    }
                  >
                    <SelectTrigger data-testid="category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Residencial">Residencial</SelectItem>
                      <SelectItem value="Comercial">Comercial</SelectItem>
                      <SelectItem value="Industrial">Industrial</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tipo de Propiedad *
                  </label>
                  <Select
                    value={formData.property_type}
                    onValueChange={(value) =>
                      setFormData({ ...formData, property_type: value })
                    }
                  >
                    <SelectTrigger data-testid="property-type-select">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PROPERTY_TYPES[formData.category]?.map((type) => (
                        <SelectItem key={type} value={type}>
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Estado *
                  </label>
                  <Select
                    value={formData.estado}
                    onValueChange={(value) =>
                      setFormData({ ...formData, estado: value })
                    }
                  >
                    <SelectTrigger data-testid="estado-select">
                      <SelectValue placeholder="Selecciona..." />
                    </SelectTrigger>
                    <SelectContent>
                      {ESTADO_OPTIONS.map((e) => (
                        <SelectItem key={e.value} value={e.value}>
                          {e.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tipo de Operación *{" "}
                    <span className="text-muted-foreground font-normal">
                      (selecciona uno o más)
                    </span>
                  </label>
                  <div
                    className="flex flex-wrap gap-3"
                    data-testid="status-select"
                  >
                    {OPERATION_TYPES.map((op) => {
                      const checked = formData.operation_types.includes(
                        op.value,
                      );
                      return (
                        <button
                          key={op.value}
                          type="button"
                          onClick={() => {
                            const current = formData.operation_types;
                            let next;
                            if (checked) {
                              next = current.filter((t) => t !== op.value);
                              if (next.length === 0) return; // keep at least one
                            } else {
                              next = [...current, op.value];
                            }
                            setFormData({ ...formData, operation_types: next });
                          }}
                          className={`px-4 py-2 rounded-full border-2 text-sm font-medium transition-all ${
                            checked
                              ? "border-primary bg-primary text-primary-foreground"
                              : "border-input bg-background hover:border-primary/60"
                          }`}
                        >
                          {op.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h2
                className="text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Ubicación
              </h2>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Dirección *
                </label>
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(text) =>
                    setFormData({ ...formData, address: text })
                  }
                  onSelect={({ address, coordinates, estado }) => {
                    setFormData((prev) => ({
                      ...prev,
                      address,
                      geometry: { type: "Point", coordinates },
                      ...(estado && !prev.estado ? { estado } : {}),
                    }));
                  }}
                  placeholder="Buscar dirección con Mapbox..."
                  required
                  dataTestId="address-autocomplete-wrapper"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Tipo de Ubicación *
                </label>
                <Input
                  type="text"
                  value={formData.location_type}
                  onChange={(e) =>
                    setFormData({ ...formData, location_type: e.target.value })
                  }
                  placeholder="Ej: Zona Metropolitana, Zona Campestre, Ciudad, Municipio"
                  required
                  data-testid="location-type-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Latitud (Norte/Sur)
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.geometry.coordinates[1]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        geometry: {
                          ...formData.geometry,
                          coordinates: [
                            formData.geometry.coordinates[0],
                            parseFloat(e.target.value) || 25.6866,
                          ],
                        },
                      })
                    }
                    placeholder="25.6866"
                    data-testid="latitude-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Longitud (Oeste/Este)
                  </label>
                  <Input
                    type="number"
                    step="any"
                    value={formData.geometry.coordinates[0]}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        geometry: {
                          ...formData.geometry,
                          coordinates: [
                            parseFloat(e.target.value) || -100.3161,
                            formData.geometry.coordinates[1],
                          ],
                        },
                      })
                    }
                    placeholder="-100.3161"
                    data-testid="longitude-input"
                  />
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                💡 <strong>Cómo obtener coordenadas:</strong> Abre Google Maps →
                busca tu dirección → click derecho en el punto exacto → click en
                las coordenadas que aparecen → se copian automáticamente
                (formato: latitud, longitud).
              </p>
            </div>

            <div className="space-y-6">
              <h2
                className="text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Características
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tamaño Terreno (m²)
                  </label>
                  <Input
                    type="number"
                    value={formData.land_size_m2}
                    onChange={(e) =>
                      setFormData({ ...formData, land_size_m2: e.target.value })
                    }
                    placeholder="0"
                    data-testid="land-size-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Tamaño Construcción (m²)
                  </label>
                  <Input
                    type="number"
                    value={formData.construction_size_m2}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        construction_size_m2: e.target.value,
                      })
                    }
                    placeholder="0"
                    data-testid="construction-size-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Recámaras
                  </label>
                  <Input
                    type="number"
                    value={formData.bedrooms}
                    onChange={(e) =>
                      setFormData({ ...formData, bedrooms: e.target.value })
                    }
                    placeholder="0"
                    data-testid="bedrooms-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Baños Completos
                  </label>
                  <Input
                    type="number"
                    value={formData.bathrooms}
                    onChange={(e) =>
                      setFormData({ ...formData, bathrooms: e.target.value })
                    }
                    placeholder="0"
                    data-testid="bathrooms-input"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    Medios Baños
                  </label>
                  <Input
                    type="number"
                    value={formData.half_bathrooms}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        half_bathrooms: e.target.value,
                      })
                    }
                    placeholder="0"
                    data-testid="half-bathrooms-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Estacionamientos
                </label>
                <Input
                  type="number"
                  value={formData.parking}
                  onChange={(e) =>
                    setFormData({ ...formData, parking: e.target.value })
                  }
                  placeholder="0"
                  data-testid="parking-input"
                />
              </div>
            </div>

            <div className="space-y-6">
              <h2
                className="text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Detalles Adicionales
              </h2>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Características (separadas por comas)
                </label>
                <Textarea
                  value={formData.features}
                  onChange={(e) =>
                    setFormData({ ...formData, features: e.target.value })
                  }
                  placeholder="Ej: Piscina, Jardín, Terraza"
                  rows={3}
                  data-testid="features-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  Amenidades (separadas por comas)
                </label>
                <Textarea
                  value={formData.amenities}
                  onChange={(e) =>
                    setFormData({ ...formData, amenities: e.target.value })
                  }
                  placeholder="Ej: Gym, Seguridad 24/7, Área de juegos"
                  rows={3}
                  data-testid="amenities-input"
                />
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  🖼️ Imagen de Portada (URL)
                </label>
                <Input
                  type="url"
                  value={formData.cover_image_url}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      cover_image_url: e.target.value,
                    })
                  }
                  placeholder="https://ejemplo.com/portada.jpg"
                  data-testid="cover-image-url-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Esta será la imagen principal que se muestra en las listas
                </p>
              </div>

              <div>
                <label className="text-sm font-medium mb-2 block">
                  🎞️ URLs del Slideshow (separadas por comas)
                </label>
                <Textarea
                  value={formData.image_urls}
                  onChange={(e) =>
                    setFormData({ ...formData, image_urls: e.target.value })
                  }
                  placeholder="Ej: https://ejemplo.com/imagen1.jpg, https://ejemplo.com/imagen2.jpg, https://ejemplo.com/imagen3.jpg"
                  rows={3}
                  data-testid="image-urls-input"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Estas imágenes se mostrarán en el carrusel de la página de
                  detalle
                </p>
              </div>
            </div>

            {/* Vincular a Desarrollo (opcional) */}
            {myDevelopments.length > 0 && (
              <div className="space-y-6">
                <h2
                  className="text-2xl font-bold"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  Vincular a un Desarrollo (Opcional)
                </h2>
                <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-4">
                  <p className="text-sm text-muted-foreground mb-3">
                    Si esta propiedad forma parte de un desarrollo, selecciónalo
                    aquí. Aparecerá en la página de detalle del desarrollo.
                  </p>
                  <Select
                    value={formData.development_id || "none"}
                    onValueChange={(v) =>
                      setFormData({
                        ...formData,
                        development_id: v === "none" ? "" : v,
                      })
                    }
                  >
                    <SelectTrigger data-testid="development-select">
                      <SelectValue placeholder="Selecciona un desarrollo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Sin desarrollo</SelectItem>
                      {myDevelopments.map((dev) => (
                        <SelectItem key={dev.id} value={dev.id}>
                          {dev.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            <div className="space-y-6">
              <h2
                className="text-2xl font-bold"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Opciones de Representación por Brokers
              </h2>

              <div className="bg-accent/10 border border-accent/30 rounded-lg p-4">
                <div className="flex items-start space-x-3 mb-4">
                  <input
                    type="checkbox"
                    id="allow-broker-representation"
                    checked={formData.allow_broker_representation}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        allow_broker_representation: e.target.checked,
                      })
                    }
                    className="mt-1 h-4 w-4"
                    data-testid="allow-broker-checkbox"
                  />
                  <div className="flex-1">
                    <label
                      htmlFor="allow-broker-representation"
                      className="font-medium cursor-pointer"
                    >
                      Permitir representación por brokers (Co-listing)
                    </label>
                    <p className="text-sm text-muted-foreground mt-1">
                      Los brokers certificados podrán solicitar representar esta
                      propiedad. Esto aumenta la visibilidad de tu propiedad y
                      las posibilidades de venta.
                    </p>
                  </div>
                </div>

                {formData.allow_broker_representation && (
                  <div className="mt-4 space-y-4 border-t border-accent/30 pt-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          💰 Comisión Base (%)
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={formData.commission_percentage}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              commission_percentage:
                                parseFloat(e.target.value) || 3.0,
                            })
                          }
                          placeholder="3.0"
                          data-testid="commission-percentage-input"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          Comisión inicial sugerida. Podrás negociarla con cada
                          broker al aprobar sus solicitudes.
                        </p>
                      </div>

                      <div>
                        <label className="text-sm font-medium mb-2 block">
                          👥 Máximo de Brokers
                        </label>
                        <Select
                          value={formData.max_brokers.toString()}
                          onValueChange={(value) =>
                            setFormData({
                              ...formData,
                              max_brokers: parseInt(value),
                            })
                          }
                        >
                          <SelectTrigger data-testid="max-brokers-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">
                              1 Broker (Exclusivo)
                            </SelectItem>
                            <SelectItem value="2">2 Brokers</SelectItem>
                            <SelectItem value="3">
                              3 Brokers (Recomendado)
                            </SelectItem>
                            <SelectItem value="4">4 Brokers</SelectItem>
                            <SelectItem value="5">5 Brokers</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          Número máximo de brokers que pueden representar esta
                          propiedad simultáneamente.
                        </p>
                      </div>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded p-3">
                      <p className="text-xs text-blue-800">
                        <strong>ℹ️ Nota sobre Co-listing:</strong> La comisión
                        se acuerda con cada broker al aprobar su solicitud y
                        queda bloqueada automáticamente para garantizar
                        seguridad. El broker que cierre la venta recibirá la
                        comisión acordada.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate("/dashboard")}
                data-testid="cancel-button"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? "Creando..." : "Crear Propiedad"}
              </Button>
            </div>
          </form>
        </motion.div>
      </div>

      <Footer />
    </div>
  );
};

export default AddProperty;
